/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

// Component imports
import { Avatar, Col, Row, Tooltip, Typography } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import ConversationStatusTag from './ConversationStatusTag';
import { ArrowRightIcon } from '../Icons/ArrowRightIcon';

// Misc imports
import { REQUEST_STATUSES } from '../../constants/liveChat';
import { getCurrentQueryState } from '../../store/utils/getCurrentQueryState';
import { LiveChatContext } from '../../screens/LiveChatScreen';
import useTheme from '../../utils/hooks/useTheme';
import formatEndUserName from '../../utils/string/formatEndUserName';
import formatTimestamp from '../../utils/misc/formatTimestamp';

const IncomingRequest = ({
  status,
  requestId,
  tenantImageUrl,
  requestedDate,
  userName,
  type,
  acceptedBy = undefined,
  request
}) => {
  const { selectedRequest, setSelectedRequest, setInboxOpen, isMobileSize, setSidebarOpen } = useContext(LiveChatContext);
  const { t } = useTranslation();
  const { orgId } = useParams();
  const { data: organization } = getCurrentQueryState('fetchOrganization', { orgId });
  const { borderColorBase } = useTheme();

  const orgTimezone = organization?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;

  const renderStatusTag = () => {
    let tags = [];
    if (status === REQUEST_STATUSES.ACCEPTED) {
      tags.push(
        <Tooltip
          key="accepted"
          title={acceptedBy}
          mouseEnterDelay={0.5}
        >
          <ConversationStatusTag
            type="accepted"
            title={
              <Typography.Text ellipsis className="text-inherit" style={{ maxWidth: 100 }}>
                {acceptedBy ?? t('Unknown user')}
              </Typography.Text>
            }
          />
        </Tooltip>
      );
    } else if (status === REQUEST_STATUSES.MISSED) {
      tags.push(<ConversationStatusTag type="missed" />);
    } else {
      tags.push(<ConversationStatusTag type="new" />);
    }
    if (type === 'transfer') {
      tags.push(<ConversationStatusTag type="transfer" />);
    } else if (type === 'invitation') {
      tags.push(<ConversationStatusTag type="joined" title={t('Invite')} />);
    }
    return tags;
  };

  const MemoizedRequestItem = useMemo(() => (
    <Row
      className={classNames('inbox-item request', {
        'selected-inbox-item': selectedRequest?.id === requestId
      })}
      wrap={false}
      align="middle"
      onClick={() => {
        setSelectedRequest(request);
        setInboxOpen(false);
        isMobileSize && setSidebarOpen(false);
      }}
    >
      <Col
        flex="none"
      >
        <Avatar.Group
          className="v-align-middle"
        >
          <Avatar
            size={44}
            className="fs-lg text-color-default fw-300 border-0"
            style={{ backgroundColor: borderColorBase }}
          >
            {
              userName.trim() && userName
                ? formatEndUserName(userName)
                : <UserOutlined/>
            }
          </Avatar>

          <Avatar
            src={tenantImageUrl}
            className="mt-auto"
            style={{ borderWidth: 3 }}
            size={28}
          />
        </Avatar.Group>
      </Col>
      <Col
        flex="auto"
        className="px-4"
      >
        <Typography.Paragraph
          className="mb-1"
          ellipsis
          strong
          title={`${t('From')}: ${userName || t('Unknown user')}`}
        >
          {userName || t('Unknown user')}
        </Typography.Paragraph>
        <Typography.Paragraph
          strong
          ellipsis
          className="mb-0"
        >
          {renderStatusTag()}
        </Typography.Paragraph>
      </Col>
      <Col>
        <Typography.Text
          ellipsis
          className="text-secondary fs-sm"
        >
          {formatTimestamp(requestedDate, false, orgTimezone)}
        </Typography.Text>
      </Col>
      {
        status === REQUEST_STATUSES.NEW
          ? <Col flex="0 0 32px">
            <ArrowRightIcon
              className="fs-lg text-primary float-right"
              style={{ marginTop: 2 }}
            />
          </Col>
          : null
      }
    </Row>
  ), [selectedRequest, status, acceptedBy, isMobileSize]);

  return MemoizedRequestItem;
};

IncomingRequest.propTypes = {
  status: PropTypes.string,
  requestId: PropTypes.number,
  tenantImageUrl: PropTypes.string,
  requestedDate: PropTypes.string,
  userName: PropTypes.string,
  type: PropTypes.string,
  acceptedBy: PropTypes.string,
  request: PropTypes.object
};

export default IncomingRequest;
